import { useMutation, useQueryClient } from '@tanstack/react-query'

import { usePostHog } from '@posthog/react'
import { toast } from 'sonner'

import { captureException } from '@/lib/sentry/ui-error-reporter'

import { isApiError } from '@/shared/api/utils/error'
import { useCurrentTenant } from '@/shared/auth/store'

import {
	createOutboundNotification,
	deleteOutboundNotification,
	markNotificationsAsRead,
	updateNotificationPreference,
	updateOutboundNotification,
} from './api'
import { notificationsKeys } from './queries'

import type {
	CreateOutboundNotificationPayload,
	NotificationChannelPreferences,
	NotificationPreferencesDTO,
	NotificationPreferenceUpdate,
	UpdateOutboundNotificationPayload,
} from './types'

export const notificationsMutations = {
	all: ['notifications'],
	markNotificationsAsRead: () => [...notificationsMutations.all, 'read'],
	updateNotificationPreference: () => [
		...notificationsMutations.all,
		'preference',
	],
}

/**
 * Mark notifications as read
 */
export const useMarkNotificationsAsRead = () => {
	const posthog = usePostHog()

	return useMutation({
		mutationKey: notificationsMutations.markNotificationsAsRead(),
		mutationFn: (notificationIds: Array<string>) =>
			markNotificationsAsRead(notificationIds),
		meta: {
			invalidates: [notificationsKeys.lists()],
			awaitInvalidation: true,
		},

		onSuccess: (_data, notificationIds) => {
			posthog.capture('mark_notifications_as_read', {
				notification_ids: notificationIds,
			})
		},

		onError: (error) => {
			const err = isApiError(error) ? error : undefined

			captureException(error, {
				source: 'react-query',
				feature: 'notifications',
				action: 'mark_notifications_as_read',
				component: 'mutations.tsx',
			})

			toast.error('Failed to mark notifications as read', {
				description: err?.message,
			})
		},
	})
}

/**
 * Update a single notification preference for a (tenant, channel) pair with
 * an optimistic cache write. Callers send only the field being toggled —
 * omitted `email`/`dashboard` keys are preserved server-side.
 *
 * The cache is channel-grouped (`NotificationPreferencesDTO`). If the channel
 * or tenant row is missing (user has not yet subscribed), it is created
 * in-place so the UI reflects the toggle immediately.
 */
export const useUpdateNotificationPreference = () => {
	const queryClient = useQueryClient()
	const posthog = usePostHog()

	return useMutation({
		mutationKey: notificationsMutations.updateNotificationPreference(),
		mutationFn: (payload: NotificationPreferenceUpdate) =>
			updateNotificationPreference(payload),

		onMutate: async (payload) => {
			await queryClient.cancelQueries({
				queryKey: notificationsKeys.preferences(),
			})

			const previous = queryClient.getQueryData<NotificationPreferencesDTO>(
				notificationsKeys.preferences(),
			)

			queryClient.setQueryData<NotificationPreferencesDTO>(
				notificationsKeys.preferences(),
				(old) => {
					const base: NotificationPreferencesDTO = old ?? { preferences: [] }
					const channelIndex = base.preferences.findIndex(
						(c) => c.channel_name === payload.channel_name,
					)

					const nextPreferences =
						channelIndex === -1
							? [
									...base.preferences,
									{
										channel_name: payload.channel_name,
										tenants: [
											{
												tenant: payload.tenant,
												email: payload.email ?? false,
												dashboard: payload.dashboard ?? false,
											},
										],
									} satisfies NotificationChannelPreferences,
								]
							: base.preferences.map((channel, index) => {
									if (index !== channelIndex) return channel

									const tenantIndex = channel.tenants.findIndex(
										(t) => t.tenant === payload.tenant,
									)

									const nextTenants =
										tenantIndex === -1
											? [
													...channel.tenants,
													{
														tenant: payload.tenant,
														email: payload.email ?? false,
														dashboard: payload.dashboard ?? false,
													},
												]
											: channel.tenants.map((tenant, tIndex) =>
													tIndex === tenantIndex
														? {
																...tenant,
																...(payload.email !== undefined && {
																	email: payload.email,
																}),
																...(payload.dashboard !== undefined && {
																	dashboard: payload.dashboard,
																}),
															}
														: tenant,
												)

									return { ...channel, tenants: nextTenants }
								})

					return { preferences: nextPreferences }
				},
			)

			return { previous }
		},

		onSuccess: (_data, payload) => {
			const type: 'email' | 'dashboard' =
				payload.email !== undefined ? 'email' : 'dashboard'
			const enabled =
				type === 'email' ? payload.email === true : payload.dashboard === true

			posthog.capture('notification_preference_toggled', {
				tenant: payload.tenant,
				channel: payload.channel_name,
				type,
				enabled,
			})
		},

		onError: (error, _payload, context) => {
			if (context?.previous) {
				queryClient.setQueryData(
					notificationsKeys.preferences(),
					context.previous,
				)
			}

			const err = isApiError(error) ? error : undefined

			captureException(error, {
				source: 'react-query',
				feature: 'notifications',
				action: 'update_notification_preference',
				component: 'mutations.tsx',
			})

			toast.error('Failed to update notification preference', {
				description: err?.message,
			})
		},

		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: notificationsKeys.preferences(),
			})
		},
	})
}

export const outboundMutations = {
	all: ['notifications', 'outbound'] as const,
	create: (tenant: string) =>
		[...outboundMutations.all, tenant, 'create'] as const,
	update: (tenant: string) =>
		[...outboundMutations.all, tenant, 'update'] as const,
	delete: (tenant: string) =>
		[...outboundMutations.all, tenant, 'delete'] as const,
}

/**
 * Create a custom outbound notification recipient for the current tenant.
 * Errors are surfaced to the caller via mutate's onError — the form maps 409s
 * to the email field via form.setFieldMeta.
 */
export const useCreateOutboundNotification = () => {
	const tenant = useCurrentTenant()
	const posthog = usePostHog()

	return useMutation({
		mutationKey: outboundMutations.create(tenant),
		mutationFn: (payload: CreateOutboundNotificationPayload) =>
			createOutboundNotification(tenant, payload),
		meta: {
			invalidates: [notificationsKeys.outbound(tenant)],
		},
		onSuccess: (_data, payload) => {
			posthog.capture('custom_receiver_created', {
				tenant,
				selected_channels: payload.channels,
			})
			toast.success('Recipient added')
		},
		onError: (error) => {
			captureException(error, {
				source: 'react-query',
				feature: 'notifications',
				action: 'create_outbound_notification',
				component: 'mutations.tsx',
			})
		},
	})
}

/**
 * Update an existing custom outbound notification recipient. Errors are
 * surfaced to the caller via mutate's onError — the form maps 409s to the
 * email field via form.setFieldMeta.
 */
export const useUpdateOutboundNotification = () => {
	const tenant = useCurrentTenant()
	const posthog = usePostHog()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: outboundMutations.update(tenant),
		mutationFn: ({
			id,
			payload,
		}: {
			id: string
			payload: UpdateOutboundNotificationPayload
		}) => updateOutboundNotification(tenant, id, payload),
		meta: {
			invalidates: [notificationsKeys.outbound(tenant)],
		},
		onSuccess: (data, { id, payload }) => {
			posthog.capture('custom_receiver_updated', {
				tenant,
				id,
				selected_channels:
					payload.channels ?? data.outbound_notification.channels,
			})
			toast.success('Recipient updated')
		},
		onError: (error) => {
			captureException(error, {
				source: 'react-query',
				feature: 'notifications',
				action: 'update_outbound_notification',
				component: 'mutations.tsx',
			})
		},
		onSettled: async (_data, _error, { id }) => {
			await queryClient.invalidateQueries({
				queryKey: notificationsKeys.outboundDetail(tenant, id),
			})
		},
	})
}

/**
 * Delete a custom outbound notification recipient.
 */
export const useDeleteOutboundNotification = () => {
	const tenant = useCurrentTenant()
	const posthog = usePostHog()

	return useMutation({
		mutationKey: outboundMutations.delete(tenant),
		mutationFn: (id: string) => deleteOutboundNotification(tenant, id),
		meta: {
			invalidates: [notificationsKeys.outbound(tenant)],
			awaitInvalidation: true,
		},
		onSuccess: (_data, id) => {
			posthog.capture('custom_receiver_deleted', { tenant, id })
			toast.success('Recipient deleted')
		},
		onError: (error) => {
			const err = isApiError(error) ? error : undefined

			captureException(error, {
				source: 'react-query',
				feature: 'notifications',
				action: 'delete_outbound_notification',
				component: 'mutations.tsx',
			})

			toast.error('Failed to delete recipient', {
				description: err?.message || 'An unknown error occurred',
			})
		},
	})
}
